// socket.js
const baseURL = import.meta.env.VITE_APP_SOCKET
const EventTypes = ['open', 'close', 'message', 'error', 'reconnect']
const DEFAULT_CHECK_TIME = 5 * 1000 // 心跳检测的默认时间
const DEFAULT_RECONNECT_COUNT = import.meta.env.MODE === 'development' ? 5 : 30 // 最大重连次数
const DEFAULT_RECONNECT_DELAY = 3 * 1000 // 重连间隔
const DEFAULT_CHECK_DATA = 'ping'

class EventMap {
  deps = new Map()

  depend(eventType, callback) {
    this.deps.set(eventType, callback)
  }

  notify(eventType, ...data) {
    if (this.deps.has(eventType)) {
      this.deps.get(eventType)(...data)
    }
  }
}

class Socket extends WebSocket {
  heartCheckData = DEFAULT_CHECK_DATA
  heartCheckTimeout = DEFAULT_CHECK_TIME
  heartCheckInterval = null
  maxReconnectCount = DEFAULT_RECONNECT_COUNT
  reconnectDelay = DEFAULT_RECONNECT_DELAY

  isOpen = false
  _manualClose = false
  _reconnectTimer = null

  constructor(options, dep, reconnectCount = 0) {
    const { url, greet = null } = options

    super(url)
    this._currentOptions = options
    this._dep = dep
    this._reconnectCount = reconnectCount
    greet &&
      Object.assign(this, {
        heartCheckData: greet,
      })
    this.initSocket()
  }

  // 初始化WebSocket
  initSocket() {
    // 监听webSocket的事件
    this.onopen = function (e) {
      this.isOpen = true
      this._reconnectCount = 0
      this._dep.notify('open', e)
      this.resetHeartCheck()
    }
    this.onclose = function (e) {
      this.isOpen = false
      this.clearHeartCheck()
      this._dep.notify('close', e)
      console.info('WebSocket关闭', e)

      if (this._manualClose) return

      if (this._reconnectCount < this.maxReconnectCount) {
        this._reconnectCount++
        this._reconnectTimer = setTimeout(() => {
          const _socket = new Socket(
            this._currentOptions,
            this._dep,
            this._reconnectCount,
          )
          this._dep.notify('reconnect', _socket)
        }, this.reconnectDelay)
      } else {
        console.info('WebSocket重连失败, 请联系技术客服!')
      }
    }
    this.onerror = function (e) {
      this._dep.notify('error', e)
    }
    this.onmessage = function (e) {
      this._dep.notify('message', e.data)
    }
  }

  // 订阅事件
  subscribe(eventType, callback) {
    if (typeof callback !== 'function')
      throw new Error('The second param is must be a function')
    if (!EventTypes.includes(eventType))
      throw new Error('The first param is not supported')
    this._dep.depend(eventType, callback)
  }

  sendMessage(data) {
    return this.send(data)
  }

  // 关闭WebSocket（主动关闭，不触发重连）
  closeSocket(code, reason) {
    this._manualClose = true
    clearTimeout(this._reconnectTimer)
    this.close(code, reason)
  }

  // 开始心跳检测
  heartCheckStart() {
    this.heartCheckInterval = setInterval(() => {
      if (this.readyState === this.OPEN) {
        this.sendMessage(this.heartCheckData)
      } else {
        this.clearHeartCheck()
      }
    }, this.heartCheckTimeout)
  }

  // 清除心跳检测
  clearHeartCheck() {
    clearInterval(this.heartCheckInterval)
  }

  // 重置心跳检测
  resetHeartCheck() {
    clearInterval(this.heartCheckInterval)
    this.heartCheckStart()
  }
}

// 默认的配置项
const defaultOptions = {
  url: baseURL,
}

export const useSocket = (options = defaultOptions) => {
  if (!window.WebSocket)
    return console.info('您的浏览器不支持WebSocket, 请更换浏览器!')
  const dep = new EventMap()
  const reconnectCount = 0
  return new Socket(options, dep, reconnectCount)
}
